import { useLocation, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

/**
 * The token of an email link (08 §2.2.2, §2.6). It travels in the fragment, `#token=…` (`AM-190`):
 * a browser never sends the fragment, so the token reaches neither the edge's access log nor a
 * `Referer`. Links mailed before the change carry it in the query string and are still honoured.
 */
export function linkTokenOf(hash: string, search: Record<string, unknown>): string | undefined {
  const fromFragment = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash).get(
    'token',
  );
  if (fromFragment) {
    return fromFragment;
  }

  const fromQuery = search['token'];
  return typeof fromQuery === 'string' && fromQuery.length > 0 ? fromQuery : undefined;
}

/**
 * Reads the link's token once, then takes it out of the address bar so it stays neither in the
 * history nor on screen for whoever looks over the shoulder.
 */
export function useLinkToken(): string | undefined {
  const location = useLocation();
  const navigate = useNavigate();
  const [token] = useState(() =>
    linkTokenOf(location.hash, location.search as Record<string, unknown>),
  );

  useEffect(() => {
    if (token !== undefined) {
      void navigate({ to: location.pathname, search: {}, hash: '', replace: true });
    }
    // Only on arrival: the token is read once and the address is cleaned once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return token;
}
